import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm, type SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CustomCombobox, InputField, TextareaInputField } from "@/components/shared/form";
import type { ComboboxOption } from "@/components/shared/form/CustomCombobox";
import { useQuery } from "@apollo/client/react";
import {
  Document,
  DocumentType,
  FetchDocumentsByTenderIdQuery,
  FetchDocumentsByTenderIdQueryVariables,
  FetchDocumentsByTenderIdDocument,
} from "@/graphql/__generated__/graphql";
import { CircleLoader } from "@/components/shared/loaders";
import { XIcon, Info } from "lucide-react";
import { TFunction } from "i18next";
import { getAuthHeader } from "@/components/document-chat/utils";
import { CustomToastComponent } from "@/components/shared";
import { toast } from "react-toastify";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL as string;

/**
 * Creates a Zod schema for the new sheet agent form
 * @param {TFunction} t - Translation function
 * @returns {z.ZodObject} The Zod schema
 */
const createNewSheetAgentSchema = (t: TFunction) => {
  return z.object({
    tenderId: z.number().min(1, t("schemas:form.required")),
    documentIds: z
      .array(z.number())
      .min(1, t("schemas:form.required")),
    name: z.string().min(1, t("schemas:form.required")),
    description: z.string().optional(),
  });
};

interface NewSheetAgentDialogProps {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  onSuccess?: () => void;
}

/**
 * Dialog component for creating a new sheet agent by selecting documents
 * @param {NewSheetAgentDialogProps} props - Component props
 * @returns {JSX.Element} The NewSheetAgentDialog component
 */
function NewSheetAgentDialog({ open, onOpenChange, onSuccess }: NewSheetAgentDialogProps) {
  const { t } = useTranslation(["modals", "common"]);
  const { tenderID } = useParams<{ tenderID: string }>();
  const tenderId = Number(tenderID);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const readerRef = useRef<ReadableStreamDefaultReader<string> | null>(null);

  const FormSchema = useMemo(() => createNewSheetAgentSchema(t), [t]);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      tenderId,
      documentIds: [] as number[],
      name: "",
      description: "",
    },
    mode: "onChange",
  });

  // Fetch XLSX documents when dialog opens
  const { data, loading } = useQuery<
    FetchDocumentsByTenderIdQuery,
    FetchDocumentsByTenderIdQueryVariables
  >(FetchDocumentsByTenderIdDocument, {
    variables: { tenderId, type: DocumentType.PriceForm },
    fetchPolicy: "cache-and-network",
    skip: !open,
  });

  const documents = data?.fetchDocumentsByTenderId || [];

  // Convert documents to combobox options
  const documentOptions: ComboboxOption[] = useMemo(
    () =>
      documents.map((doc: Document) => ({
        label: doc.fileName,
        value: doc.id,
      })),
    [documents]
  );

  // Get selected document IDs from form
  const selectedDocumentIds = form.watch("documentIds") || [];

  // Get selected documents for display
  const selectedDocuments = useMemo(
    () => documents.filter((doc: Document) => selectedDocumentIds.includes(doc.id)),
    [documents, selectedDocumentIds]
  );

  // Cleanup function to abort fetch and cancel reader on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (readerRef.current) {
        readerRef.current.cancel().catch(() => {
          // Ignore cancellation errors
        });
      }
    };
  }, []);

  // Clear form when dialog opens
  useEffect(() => {
    if (open) {
      form.reset({
        tenderId,
        documentIds: [] as number[],
        name: "",
        description: "",
      });
      setIsSubmitting(false);
    }
  }, [open, form, tenderId]);

  const onSubmit: SubmitHandler<z.infer<typeof FormSchema>> = async (data) => {
    setIsSubmitting(true);

    // Create and store abort controller
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const response = await fetch(`${BACKEND_URL}/sheet/answer/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          Authorization: getAuthHeader(),
        },
        body: JSON.stringify({
          tenderId: data.tenderId,
          documentIds: data.documentIds,
          name: data.name,
          description: data.description,
        }),
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      if (!response.body) {
        throw new Error("ReadableStream not supported in this browser.");
      }

      // Read the SSE stream and wait for the first event
      const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
      readerRef.current = reader;
      let buffer = "";
      let firstEventReceived = false;

      try {
        while (!firstEventReceived) {
          const { value, done } = await reader.read();

          if (done) {
            // Stream ended before receiving first event
            break;
          }

          if (value) {
            buffer += value;

            // Check if we have a complete SSE event (events are separated by \n\n)
            const eventEndIndex = buffer.indexOf("\n\n");
            if (eventEndIndex !== -1) {
              // We received at least one complete event
              firstEventReceived = true;
              // Close the reader since we only need the first event
              await reader.cancel();
              break;
            }
          }
        }
      } catch (streamError) {
        // Ignore AbortError as it's expected when component unmounts
        if ((streamError as Error).name !== "AbortError") {
          console.warn("Error reading SSE stream:", streamError);
        }
      } finally {
        // Clean up reader ref
        readerRef.current = null;
      }

      // Only call callbacks after receiving the first event (or if stream ended)
      // This ensures the data is in the database before refetching
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      // Ignore AbortError as it's expected when component unmounts
      if ((error as Error).name === "AbortError") {
        return;
      }
      console.error("Failed to submit sheet agent:", error);
      toast(CustomToastComponent, {
        toastId: "sheet-agent-run-failed-to-submit",
        data: {
          title: t("toasts.somethingwentwrong"),
          content: (error as Error).message,
          type: "error",
        },
      });
    } finally {
      setIsSubmitting(false);
      // Clean up abort controller ref
      abortControllerRef.current = null;
    }
  };

  const onInvalid = (errors: unknown) => {
    console.error(errors);
  };

  const handleRemoveDocument = (documentId: number) => {
    const currentIds = form.getValues("documentIds") || [];
    form.setValue(
      "documentIds",
      currentIds.filter((id) => id !== documentId),
      { shouldValidate: true }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[90vw] lg:max-w-2xl overflow-y-auto max-h-[90vh]">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="space-y-6">
            <DialogHeader>
              <DialogTitle>{t("sheetagent.newrun.title")}</DialogTitle>
              <DialogDescription>
                {t("sheetagent.newrun.description")}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <Alert>
                <Info />
                <AlertTitle>{t("sheetagent.newrun.alert.uploaddocumentstitle")}</AlertTitle>
                <AlertDescription>
                  <p>
                    {t("sheetagent.newrun.alert.uploaddocumentsdescription")}{" "}
                    <Link to={`/tender/${tenderID}/documents`} className="text-blue-500 hover:underline">
                      {t("sheetagent.newrun.alert.documentspagelink")}
                    </Link>
                    .
                  </p>
                </AlertDescription>
              </Alert>

              <InputField
                form={form}
                name="name"
                label={t("sheetagent.newrun.inputs.name.label")}
                placeholder={t("sheetagent.newrun.inputs.name.placeholder")}
                className="w-full"
                disabled={isSubmitting}
              />

              <TextareaInputField
                form={form}
                name="description"
                label={t("sheetagent.newrun.inputs.description.label")}
                placeholder={t("sheetagent.newrun.inputs.description.placeholder")}
                showOptional={true}
                formItemClassName="w-full"
                disabled={isSubmitting}
              />

              <FormField
                control={form.control}
                name="documentIds"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("sheetagent.newrun.inputs.documents.label")}</FormLabel>
                    <FormControl>
                      <CustomCombobox
                        options={documentOptions}
                        value={field.value || []}
                        onChange={(value) => {
                          const newValue = Array.isArray(value) ? value : value ? [value] : [];
                          field.onChange(newValue);
                        }}
                        placeholder={t("sheetagent.newrun.inputs.documents.placeholder")}
                        multiple={true}
                        loading={loading}
                        disabled={loading || isSubmitting}
                        popoverProps={{
                          onWheel: (e) => {
                            e.stopPropagation();
                          },
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Display selected documents */}
              {selectedDocuments.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedDocuments.map((doc: Document) => (
                    <Badge
                      key={doc.id}
                      variant="secondary"
                      className="flex items-center gap-1.5 px-2 py-1"
                    >
                      <span className="text-sm">{doc.fileName}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-4 w-4 p-0 hover:bg-destructive/20"
                        onClick={() => handleRemoveDocument(doc.id)}
                        disabled={isSubmitting}
                      >
                        <XIcon className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline" disabled={isSubmitting}>
                  {t("common:buttons.cancel")}
                </Button>
              </DialogClose>
              <Button type="submit" className="gap-2" disabled={isSubmitting}>
                <CircleLoader visible={isSubmitting} height={16} width={16} strokeWidth={2} />
                {t("common:buttons.create")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default NewSheetAgentDialog;
